import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Icon, { Icons } from '@/components/elements/Icon';
import { animated, useSpring } from '@react-spring/web';

export interface CollapsibleInterface {
  children: ReactNode;
  header: ReactNode;
  open?: boolean;
  gray?: 50 | 100;
}

const Collapsible = ({
  header,
  children,
  open: startOpen,
  gray = 100,
}: CollapsibleInterface) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(startOpen || false);
  const [style, animate] = useSpring(
    // if we're open at the start, don't slide down.
    () => ({
      opacity: 1,
      ...(!startOpen && { height: '0px', opacity: 0 }),
    }),
    []
  );

  const toggle = useCallback(() => {
    if (divRef.current) {
      const listHeight = divRef?.current?.offsetHeight;
      setOpen((o) => {
        animate.start({
          height: !o ? `${listHeight}px` : `0px`,
          opacity: 1,
        });

        return !o;
      });
    }
  }, [divRef, animate]);

  const bgColor = gray === 50 ? 'bg-gray-50' : 'bg-gray-100';

  return (
    <div
      className={`rounded-md border overflow-hidden bg-gray-50 border-gray-300 text-sm`}
    >
      <button
        type="button"
        onClick={toggle}
        className={`flex w-full items-stretch group ${bgColor}`}
      >
        <span className="flex-1 px-4 py-3 border-r border-gray-300 flex items-center truncate">
          {header}
        </span>
        <span className="group-hover:bg-gray-200 flex items-center justify-center min-w-[3rem] min-h-[3rem]">
          <Icon
            name={Icons.CHEVRON_DOWN}
            size={20}
            className={`${
              open ? 'rotate-180' : ''
            } transform transition-transform text-gray-400`}
          />
        </span>
      </button>
      <animated.div style={style}>
        <div
          className="border-t border-gray-300 text-gray-500 font-medium"
          ref={divRef}
        >
          {children}
        </div>
      </animated.div>
    </div>
  );
};

export default Collapsible;
